import { useId } from 'react';
import { BellIcon, BellOffIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import type { FollowReason } from '@/lib/api/generated/model';

import { useFollow } from '../hooks/useFollow';

const REASON: Partial<Record<FollowReason, string>> = {
  created: 'You created this task',
  commented: 'You commented',
};

/**
 * Follow / Following in the drawer header (spec 131 US3). Following decides
 * what reaches you about a task; an Unfollow sticks, even if you comment
 * again. The reason a follow arose is said in the tooltip and to a screen
 * reader; a follow you chose yourself needs no reason.
 */
export function FollowToggle({
  workspaceId,
  taskId,
}: {
  workspaceId: string;
  taskId: string;
}) {
  const { state, isPending, setFollowing, isSaving } = useFollow(
    workspaceId,
    taskId,
  );
  const reasonId = useId();
  if (isPending || !state) return null;
  const reason =
    state.following && state.reason ? REASON[state.reason] : undefined;
  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        aria-pressed={state.following}
        aria-describedby={reason ? reasonId : undefined}
        title={reason}
        disabled={isSaving}
        onClick={() => {
          setFollowing(!state.following);
        }}
        className="group/follow"
      >
        {state.following ? (
          <BellIcon aria-hidden="true" strokeWidth={1.5} />
        ) : (
          <BellOffIcon aria-hidden="true" strokeWidth={1.5} />
        )}
        {state.following ? (
          <>
            <span className="group-hover/follow:hidden">Following</span>
            <span className="hidden group-hover/follow:inline">Unfollow</span>
          </>
        ) : (
          'Follow'
        )}
      </Button>
      {reason ? (
        <span id={reasonId} className="sr-only">
          {reason}
        </span>
      ) : null}
    </>
  );
}
